/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */

import { useVideCut } from '@/adapters/users';
import { useCreateFragment, useLockFragment, useUpdateFragment } from '@/adapters/workbentch';
import {
  RegionDefaultColor,
  RegionProcessedColor,
  RegionProcessedSelectedColor,
  RegionSelectedColor,
} from '@/components/Videoplayer/AVDisplay';
import { ExtRegion } from '@/components/Videoplayer/Waveform';
import { WorkbenchContext } from '@/contexts/WorkbenchContext';
import {
  ApiFragment,
  ExtRegionParams,
  FragmentLockStatus,
  RenderStatusValue,
} from '@/types/workbench';
import { useLastSegmentEndTime } from '@/hooks/useLastSegmentEndTime';
import { toHHMMSS } from '@/utils';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getFragmentEndTime, getFragmentEndTimeAdjusted } from '@/lib/fragment-utils';

/**
 * Determines if a fragment is in a processed/finished state.
 * Matches the logic in ClipGrid.tsx where only 'Processed' status shows the "Processed" button.
 * @param renderStatus - The render status of the fragment
 * @returns true if the fragment is processed, false otherwise
 */
const isFragmentProcessed = (renderStatus: RenderStatusValue): boolean => {
  return renderStatus === 'SCHEDULED';
};

const createRegionParams = (
  fragment: ApiFragment,
  selected: boolean,
  lastSegmentEndTimeMs?: number,
): ExtRegionParams => {
  // Use utility function to handle null timeEndAdjusted with PeakDataSegment fallback
  const effectiveEndTime = getFragmentEndTimeAdjusted(fragment, lastSegmentEndTimeMs);

  // Determine color based on processed state and selection
  const isProcessed = isFragmentProcessed(fragment.renderStatus);
  let color: string;
  if (isProcessed) {
    color = selected ? RegionProcessedSelectedColor : RegionProcessedColor;
  } else {
    color = selected ? RegionSelectedColor : RegionDefaultColor;
  }

  // All fragment types (clips, segments, topics) can be resized/dragged on the waveform, never when locked
  const canEdit = !fragment.isLocked;

  return {
    id: fragment.id.toString(),
    start: fragment.timeStartAdjusted / 100,
    end: effectiveEndTime / 100,
    color: color,
    resize: canEdit,
    resizeStart: canEdit,
    resizeEnd: canEdit,
    drag: canEdit,
    locked: fragment.isLocked,
    fragment: fragment,
    assigned: false,
    selected: selected,
    updating: true,
  } as ExtRegionParams;
};
export interface RegionLoader {
  region: ExtRegionParams[];
  selectedRegions: ExtRegionParams[];
  setSelectedRegions(selectedRegions: ExtRegionParams[]): void;
  reloadRegions?(): void;
  handleRegionsAssigned?(): void;
  handleRegionsLocked?(): void;
  handleRegionsDeleted?(frag: ApiFragment[], regions: ExtRegionParams[]): void;
  handleRegionUpdated?(region: ExtRegion, updatedSide?: 'start' | 'end' | 'both'): void;
  handleRegionCreated?(time: number, frags: ApiFragment[]): void;
  handleRegionsVisibilityChanged?(): void;
  handleRegionsProduce?(sourceFilePath: string, regions: ExtRegionParams[]): void;
}

export function useRegions(): RegionLoader {
  const {
    workerId,
    fragments: allFragments,
    setFragments,
    regionParams: regions,
    setRegionParams: setRegions,
    setSelectedRegions,
    selectedRegions,
  } = useContext(WorkbenchContext);
  const lastSegmentEndTimeMs = useLastSegmentEndTime();
  const { mutateAsync: createFragment } = useCreateFragment(workerId);
  const { mutateAsync: updateFragmentLockStatus } = useLockFragment(workerId);
  const { mutateAsync: videoCut } = useVideCut();
  const { projectId } = useParams<{ projectId: string }>();
  const { mutateAsync: updateFragment } = useUpdateFragment(workerId);

  const fragments = useMemo(() => allFragments.filter((f) => !f.isDisabled), [allFragments]);

  // Keep a ref to the latest selectedRegions so reloadRegions always reads
  // the current value, even when called from an effect that only depends on
  // `fragments` (which would otherwise capture a stale closure).
  const selectedRegionsRef = useRef(selectedRegions);
  selectedRegionsRef.current = selectedRegions;

  useEffect(() => {
    // console.log('USE REGIONS: Fragments changed, updating Regions', fragments);
    reloadRegions();
  }, [fragments]);

  const reloadRegions = (): void => {
    // Read the latest selection from the ref (not the stale closure)
    const currentSelected = selectedRegionsRef.current;
    const regionParams = rebuildRegions(fragments, currentSelected);
    setRegions(regionParams);

    const selectedIds = new Set(currentSelected.map((r) => r.id));
    if (selectedIds.size === 0) return; // nothing was selected — don't touch selection state

    const updatedSelected = regionParams.filter((r) => selectedIds.has(r.id));

    // Only call setSelectedRegions when the matched set is non-empty.
    // During live streaming, fragments change frequently; calling
    // setSelectedRegions unconditionally would overwrite the user's
    // selection with a new array reference, causing the waveform region
    // effect in AVDisplay to re-run and potentially clear grab handles.
    if (updatedSelected.length > 0) {
      setSelectedRegions(updatedSelected);
    }
  };

  const rebuildRegions = (
    fragments: ApiFragment[],
    selectedRegions: ExtRegionParams[],
  ): ExtRegionParams[] => {
    const _selectedRegionIds = new Set(selectedRegions.map((region) => region.id));
    const result = fragments.map((fragment, index) => {
      const _selected = _selectedRegionIds.has(fragment.id.toString());
      const regionParams = createRegionParams(fragment, _selected, lastSegmentEndTimeMs);
      return regionParams;
    });
    return result;
  };

  /**
   * Handles region updates, preserving null values for unadjusted fields.
   * When updatedSide is specified, only the corresponding adjusted field is updated,
   * preserving the original value (including null) for the other field.
   * @param region - The updated region
   * @param updatedSide - Which side was adjusted: 'start', 'end', 'both', or undefined (defaults to 'both')
   */
  const handleRegionUpdated = (region: ExtRegion, updatedSide?: 'start' | 'end' | 'both'): void => {
    const fragment: ApiFragment = region.fragment;
    if (!fragment) return;

    // Check if this is a clip (stored in seconds in DB) or segment (stored in centiseconds in DB)
    const isClip = fragment.type === 'clip';

    // Default to 'both' if not specified
    const side = updatedSide || 'both';

    // Determine new adjusted values based on which side was updated
    // WaveSurfer always provides region.start/end in seconds.
    // Internal state stores ALL fragment times in centiseconds (useFragments converts
    // clips from DB seconds → centiseconds on load). So we always multiply by 100 here
    // to keep the internal state consistent.
    let newTimeStartAdjusted: number;
    let newTimeEndAdjusted: number | null;

    if (side === 'start' || side === 'both') {
      newTimeStartAdjusted = Math.round(region.start * 100);
    } else {
      // Preserve original value when start wasn't adjusted
      newTimeStartAdjusted = fragment.timeStartAdjusted;
    }

    if (side === 'end' || side === 'both') {
      newTimeEndAdjusted = Math.round(region.end * 100);
    } else {
      // Preserve original value (including null) when end wasn't adjusted
      newTimeEndAdjusted = fragment.timeEndAdjusted;
    }

    // Convert internal centisecond values back to API units:
    // Clips are stored in seconds in the DB, segments in centiseconds
    const apiTimeStart = isClip ? Math.round(fragment.timeStart / 100) : fragment.timeStart;
    const apiTimeEnd =
      fragment.timeEnd !== null
        ? isClip
          ? Math.round(fragment.timeEnd / 100)
          : fragment.timeEnd
        : null;
    const apiTimeStartAdjusted = isClip
      ? Math.round(newTimeStartAdjusted / 100)
      : newTimeStartAdjusted;
    const apiTimeEndAdjusted =
      newTimeEndAdjusted !== null
        ? isClip
          ? Math.round(newTimeEndAdjusted / 100)
          : newTimeEndAdjusted
        : null;

    const apiFragment: ApiFragment = {
      id: fragment.id,
      workerId: fragment.workerId,
      fragmentName: fragment.fragmentName,
      graduationId: fragment.graduationId,
      studentId: null,
      sequenceNumber: fragment.sequenceNumber,
      studentSeatNumber: null,
      timeStart: apiTimeStart,
      timeEnd: apiTimeEnd,
      timeStartAdjusted: apiTimeStartAdjusted,
      timeEndAdjusted: apiTimeEndAdjusted,
      isDisabled: fragment.isDisabled,
      isLocked: fragment.isLocked,
      renderStatus: fragment.renderStatus,
      renderStatusLastUpdated: new Date().toISOString(),
      type: fragment.type,
    };

    updateFragment(apiFragment as unknown as ApiFragment).catch(console.error);
    setFragments((prev) =>
      prev.map((f) =>
        f.id === fragment.id
          ? {
              ...f,
              timeStartAdjusted: newTimeStartAdjusted,
              timeEndAdjusted: newTimeEndAdjusted,
            }
          : f,
      ),
    );
  };

  const handleRegionsProduce = (sourceFilePath: string, regions: ExtRegionParams[]): void => {
    const _fragments = regions.map((region) => region.fragment);
    console.log('Producing fragments', _fragments);
    const start = regions[0].fragment.timeStart || 0;
    const end = getFragmentEndTime(regions[0].fragment, lastSegmentEndTimeMs);
    videoCut({
      start: toHHMMSS(start),
      end: toHHMMSS(end),
      projectId: projectId as string,
      segmentName: '',
    })
      .then(() => {
        regions.forEach((region) => {
          region.fragment.renderStatus = 'SCHEDULED';
        });
        setFragments([...fragments]);
      })
      .catch(console.error);
  };

  const handleRegionsLocked = (): void => {
    const _fragments = fragments.map((fragment) => {
      const apply = selectedRegions.some((r) => +(r.id || '') === fragment.id);
      if (apply) {
        fragment.isLocked = !fragment.isLocked;
      }
      return fragment;
    });
    setFragments(_fragments);
    const selectedFragments = selectedRegions.map((region) => region.fragment);
    console.log('Locking fragments', selectedFragments);
    updateFragmentLockStatus({
      idsLocked: selectedFragments
        .filter((fragment) => fragment.isLocked)
        .map((fragment) => +fragment.id),
      idsUnlocked: selectedFragments
        .filter((fragment) => !fragment.isLocked)
        .map((fragment) => +fragment.id),
    } as FragmentLockStatus).catch(console.error);
  };

  const handleRegionCreated = (time: number, frags: ApiFragment[]): void => {
    console.log('Creating region at', time, frags);
    // Use allFragments instead of frags parameter to ensure we get all existing segments
    // The frags parameter might be incomplete or filtered
    const segmentFragments = allFragments.filter((frag) => frag.type === 'segment');
    const fragment: ApiFragment = {} as ApiFragment;
    fragment.timeStart = Math.trunc(time * 100);
    fragment.timeEnd = Math.trunc((time + 10) * 100);
    fragment.timeStartAdjusted = Math.trunc(time * 100);
    fragment.timeEndAdjusted = Math.trunc((time + 10) * 100);
    fragment.isDisabled = false;
    fragment.isLocked = false;
    fragment.graduationId = null;
    fragment.workerId = workerId;
    // Use max(sequenceNumber)+1 to allow fragments with same start time (unique constraint is on workerId+sequenceNumber+type)
    // Use allFragments to get the correct max sequence number from all existing segments
    const maxSeq = segmentFragments.length
      ? Math.max(...segmentFragments.map((f) => f.sequenceNumber ?? 0))
      : 0;
    fragment.sequenceNumber = maxSeq + 1;
    fragment.fragmentName = `Segment ${fragment.sequenceNumber}`;
    fragment.type = 'segment';
    createFragment(fragment)
      .then((af: ApiFragment) => {
        console.log('ApiFragment created', af);
        // Use functional update to ensure we're working with the latest fragments state
        // This prevents race conditions with socket callbacks that might update fragments
        setFragments((prevFragments) => {
          // Check if fragment already exists (socket callback might have added it)
          const exists = prevFragments.some((f) => f.id === af.id);
          if (exists) {
            return prevFragments; // Already added by socket callback
          }
          // Add new fragment and sort
          return [...prevFragments, af].sort((a, b) => a.timeStartAdjusted - b.timeStartAdjusted);
        });
      })
      .catch(console.error);
  };

  const handleRegionsAssigned = (): void => {
    if (!selectedRegions.length) return;
    console.log('Selected Regions', selectedRegions);
    setFragments([...fragments]);
    const _lastSelected = selectedRegions[selectedRegions.length - 1];
    setSelectedRegions([createRegionParams(_lastSelected.fragment, true, lastSegmentEndTimeMs)]);
    const idx = regions.findIndex((region) => region.id === _lastSelected.id);
    const next = regions[idx + 1];
    if (next) {
      setSelectedRegions([regions[idx + 1]]);
    }
  };

  return {
    region: regions,
    selectedRegions,
    setSelectedRegions,
    handleRegionUpdated,
    handleRegionCreated,
    handleRegionsAssigned,
    handleRegionsLocked,
    handleRegionsProduce,
  };
}
